/**
 * A Rivhit card shared by several Accounts — choose its customer, or detach this
 * Account (owner design, 2026-10-25).
 *
 * Opened imperatively from any issuing flow (`openSharedCardDialog`) so the flows
 * keep their async/confirm shape: it resolves when closed with
 *   {done: false}                              — closed without a change
 *   {done: true, currentDetached: bool, count} — Accounts were detached
 *
 * The server recomputes who is detached and refuses if it differs from the list
 * shown here; Rivhit itself is never changed.
 */
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import config from '../../config/config';

const API_BASE = config.api.baseUrl;
const C = { red: '#b91c1c', sub: '#64748b', line: '#e2e8f0', green: '#047857', indigo: '#4338ca', amber: '#b45309' };

function SharedRivhitCardDialog({ rid, accountId, authHeaders, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [keep, setKeep] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dead = false;
    fetch(`${API_BASE}/api/accounting/rivhit-card/${encodeURIComponent(rid)}/accounts?account_id=${encodeURIComponent(accountId || '')}`,
      { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 403 ? 'אין הרשאה (הנהלת חשבונות / אדמין בלבד)' : `שגיאה (${r.status})`);
        return r.json();
      })
      .then((d) => { if (!dead) { setData(d); setKeep(d.recommended_account_id || ''); } })
      .catch((e) => { if (!dead) setErr(e.message); });
    return () => { dead = true; };
  }, [rid, accountId, authHeaders]);

  const accounts = data?.accounts || [];
  const keepAcc = accounts.find((a) => a.account_id === keep);
  // Same rule as the server (services/rivhit_shared_card.plan_detach).
  const planKeep = keepAcc
    ? accounts.filter((a) => a.account_id !== keep && !(keepAcc.valid_id && a.valid_id === keepAcc.valid_id)).map((a) => a.account_id)
    : [];
  const sameAsKeep = (a) => keepAcc && a.account_id !== keep && keepAcc.valid_id && a.valid_id === keepAcc.valid_id;

  const detach = async (mode) => {
    const ids = mode === 'only' ? [accountId] : planKeep;
    const msg = mode === 'only'
      ? 'לנתק את החשבון הזה מכרטיס הרווחית?\n\nבמסמך הבא רווחית תמצא את הכרטיס שלו לפי מספר לקוח / ת.ז, או תפתח כרטיס חדש בשמו.'
      : `לשמור את הכרטיס אצל "${keepAcc?.name}" ולנתק ממנו ${ids.length} חשבונות?\n\n`
        + 'בכל אחד מהם, במסמך הבא רווחית תמצא את הכרטיס שלו לפי מספר לקוח / ת.ז, או תפתח כרטיס חדש בשמו.';
    if (!ids.length || !window.confirm(msg)) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/accounting/rivhit-card/${encodeURIComponent(rid)}/detach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(mode === 'only'
          ? { mode, account_id: accountId, expected_account_ids: ids }
          : { mode, keep_account_id: keep, expected_account_ids: ids }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail?.error || `שגיאה (${r.status})`);
      onClose({ done: true, count: d.count, currentDetached: (d.detached || []).some((x) => x.account_id === accountId) });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // 2026-10-29 — keep the card on every Account: one customer in accounting
  // (home + business of one person). The tax-invoice block then lets the card be.
  const approveAll = async () => {
    const ids = accounts.map((a) => a.account_id);
    if (!window.confirm(
      `להשאיר את כרטיס ${rid} לכל ${ids.length} החשבונות?\n\n`
      + 'המשמעות: בהנהלת החשבונות אלה לקוח אחד (למשל בית ועסק של אותו אדם), וכל המסמכים של כולם יופקו על הכרטיס הזה.\n'
      + 'חשבון שיצורף לכרטיס בהמשך יצטרך אישור משלו.')) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/accounting/rivhit-card/${encodeURIComponent(rid)}/approve-shared`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ expected_account_ids: ids }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail?.error || `שגיאה (${r.status})`);
      onClose({ done: true, approved: true, count: 0, currentDetached: false });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const th = { textAlign: 'right', padding: '6px 8px', fontSize: 12, color: C.sub, borderBottom: `1px solid ${C.line}` };
  const td = { padding: '6px 8px', fontSize: 13, borderBottom: `1px solid ${C.line}` };

  return (
    <div onClick={() => !busy && onClose({ done: false })} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div dir="rtl" onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 18, width: 'min(860px, 100%)', maxHeight: '88vh', overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <b style={{ fontSize: 16 }}>כרטיס רווחית {rid} משותף לכמה חשבונות</b>
          <button onClick={() => onClose({ done: false })} disabled={busy}
            style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
        {data && (
          <div style={{ fontSize: 13, color: C.sub, marginBottom: 10 }}>
            השם בכרטיס ברווחית: <b style={{ color: '#0f172a' }}>{data.card?.name || '—'}</b>
            {data.card?.acc_ref && <> · מספר לקוח: <b style={{ color: '#0f172a' }}>{data.card.acc_ref}</b></>}
            {data.card?.id_number && data.card.id_number !== '0' && <> · ת.ז/ח.פ: <b style={{ color: '#0f172a' }}>{data.card.id_number}</b></>}
            <div style={{ marginTop: 4 }}>
              בחרו את החשבון שהכרטיס הזה שייך לו. כל שאר החשבונות ינותקו ממנו — חוץ מחשבונות עם אותה ת.ז (אותו לקוח).
              או, אם כולם לקוח אחד בהנהלת החשבונות (למשל בית ועסק), השאירו את הכרטיס לכולם. רווחית לא משתנה.
            </div>
          </div>
        )}
        {err && <div style={{ color: C.red, fontWeight: 600, marginBottom: 8 }}>{err}</div>}
        {!data && !err && <div style={{ color: C.sub }}>טוען…</div>}
        {data && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th} /><th style={th}>חשבון</th><th style={th}>ת.ז / ח.פ</th><th style={th}>מספר לקוח</th>
              <th style={th}>נוצר</th><th style={th} />
            </tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.account_id} style={{ background: a.account_id === keep ? '#ecfdf5' : sameAsKeep(a) ? '#f0fdf4' : undefined }}>
                  <td style={td}><input type="radio" name="keep" checked={keep === a.account_id} onChange={() => setKeep(a.account_id)} /></td>
                  <td style={td}>
                    <a href={`/object/Account/${a.account_id}`} target="_blank" rel="noreferrer" style={{ color: C.indigo }}>{a.name || a.account_id}</a>
                    {a.is_current && <span style={{ color: C.amber, fontWeight: 700 }}> · החשבון הנוכחי</span>}
                    {a.account_id === data.recommended_account_id && <span style={{ color: C.green, fontWeight: 700 }}> · מומלץ</span>}
                    {a.approved && <span style={{ color: C.indigo, fontWeight: 700 }}> · אושר כאותו לקוח</span>}
                  </td>
                  <td style={td}>{a.client_id || '—'}{a.client_id && !a.valid_id && <span style={{ color: C.sub }}> (לא תקינה)</span>}</td>
                  <td style={td}>{a.customer_number || '—'}</td>
                  <td style={td}>{a.created || '—'}</td>
                  <td style={{ ...td, fontSize: 12, color: C.sub }}>
                    {a.account_id === keep ? 'נשאר' : sameAsKeep(a) ? 'נשאר (אותה ת.ז)' : keepAcc ? 'ינותק' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button onClick={() => detach('keep')} disabled={busy || !keepAcc || !planKeep.length} style={{
              padding: '8px 14px', borderRadius: 8, border: 'none', background: C.green, color: '#fff', fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer', opacity: (!keepAcc || !planKeep.length) ? 0.5 : 1,
            }}>{keepAcc ? `זה הלקוח של הכרטיס — נתק ${planKeep.length} חשבונות` : 'בחרו חשבון'}</button>
            {accountId && accounts.some((a) => a.account_id === accountId) && (
              <button onClick={() => detach('only')} disabled={busy} style={{
                padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.amber}`, background: '#fff', color: C.amber,
                fontWeight: 700, cursor: busy ? 'wait' : 'pointer',
              }}>נתק רק את החשבון הזה</button>
            )}
            <button onClick={approveAll} disabled={busy || accounts.length < 2} style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.indigo}`, background: '#fff', color: C.indigo,
              fontWeight: 700, cursor: busy ? 'wait' : 'pointer',
            }}>השאר את הכרטיס לכל החשבונות — אותו לקוח בהנה"ח</button>
            <button onClick={() => onClose({ done: false })} disabled={busy} style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer',
            }}>סגור</button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Open the dialog from any async flow; resolves with its outcome. */
export function openSharedCardDialog({ rid, accountId, authHeaders }) {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const close = (result) => {
      root.unmount();
      host.remove();
      resolve(result || { done: false });
    };
    root.render(<SharedRivhitCardDialog rid={rid} accountId={accountId} authHeaders={authHeaders} onClose={close} />);
  });
}

export default SharedRivhitCardDialog;
